import { cdn } from "core/cdn"
import fr from "locales/langs/fr"
import { IStrings } from "locales/languages"

import {
  DEVICE_ID_DERMATOSCOPE,
  DEVICE_ID_OTOSCOPE,
  DEVICE_ID_STETHOSCOPE,
  DEVICE_ID_STETHOSCOPE_1,
  DEVICE_ID_STETHOSCOPE_2,
  DEVICE_ID_STETHOSCOPE_3,
  DEVICE_ID_STETHOSCOPE_4,
  UNKNOWN_DEVICE,
} from "./types/types"

export enum TUTO_STEP {
  FOLLOW_INSTRUCTIONS = -21,
  // MANAGER PLUG =>
  UNPLUGGED = -1,
  PLUGGED = 0,
  // tuto =>
  TAKE = 1,
  TURN_ON = 2,
  LIGHT = 3,
  PLACING = 4,
  FOCUS = 5,
  TURN_OFF = 6,
}

// Devices with no step-by-step tutorial: they go straight to FOLLOW_INSTRUCTIONS.
export const DO_NOT_NEED_TUTO = [
  DEVICE_ID_STETHOSCOPE,
  DEVICE_ID_STETHOSCOPE_1,
  DEVICE_ID_STETHOSCOPE_2,
  DEVICE_ID_STETHOSCOPE_3,
  DEVICE_ID_STETHOSCOPE_4,
]
// Devices the patient cannot switch off: the last step asks to put them down instead.
export const CANNOT_TURN_OFF = DO_NOT_NEED_TUTO

// Numbered steps of the tutorial: TAKE, LIGHT, PLACING, FOCUS. The kiosk header
// only counts the last three, the doctor counter includes TAKE.
export const TUTORIAL_TOTAL_STEP = 4

// Delay before TURN_ON warns that the device does not seem to work.
export const WARNING_MEDIA_DEVICE_TIMER = 20

export const deviceName = (label: string, size?: "short" | "naming") => {
  switch (label) {
    case DEVICE_ID_OTOSCOPE:
      if (size === "short") return " l'otoscope"
      if (size === "naming") return "otoscope"
      return " l'otoscope (outil A)"
    case DEVICE_ID_STETHOSCOPE:
    case DEVICE_ID_STETHOSCOPE_1:
    case DEVICE_ID_STETHOSCOPE_2:
    case DEVICE_ID_STETHOSCOPE_3:
    case DEVICE_ID_STETHOSCOPE_4:
      if (size === "short") return " le stéthoscope"
      if (size === "naming") return "stethoscope"
      return " le stéthoscope (outil B)"
    case DEVICE_ID_DERMATOSCOPE:
      if (size === "short") return " le dermatoscope"
      if (size === "naming") return "dermatoscope"
      return " le dermatoscope (outil D)"
    default:
      return UNKNOWN_DEVICE
  }
}

export const deviceImage = (label: string) => {
  switch (label) {
    case DEVICE_ID_OTOSCOPE:
      return cdn("images/devices/otoscope.svg")
    case DEVICE_ID_STETHOSCOPE:
    case DEVICE_ID_STETHOSCOPE_1:
    case DEVICE_ID_STETHOSCOPE_2:
    case DEVICE_ID_STETHOSCOPE_3:
    case DEVICE_ID_STETHOSCOPE_4:
      return cdn("images/devices/stethoscope.svg")
    case DEVICE_ID_DERMATOSCOPE:
      return cdn("images/devices/dermatoscope.svg")
    default:
      return ""
  }
}

const tutorialImage = (label: string, prefix: string) =>
  cdn(`/images/devices/tutorial_device/${prefix}_${deviceName(label, "naming")}.png`)

// The doctor reads plain French: the patient-facing strings carry <b>/<br> markup.
const plainText = (html: string) =>
  (html ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()

export interface TutorialStepDescriptor {
  /** Label the kiosk prints above the step. */
  header: string
  /** Rank in the numbered sequence sent to the doctor, 0 for the steps outside it. */
  step: number
  /** Images rendered by the kiosk, in display order. */
  images: (label: string) => string[]
  /** Patient-facing text, in the language of the kiosk. */
  text: (languages: IStrings, label: string) => string
  /** Same step in plain French, for the doctor. */
  description: (label: string) => string
  /** Blocking state to report to the doctor, empty when the step is nominal. */
  error: (label: string, deviceWarning: boolean) => string
}

/**
 * Single source of truth for the connected device tutorial.
 *
 * Both the kiosk rendering (Device.tsx) and the JSON sent to the doctor on the
 * Twilio data track read these rows, so a wording or asset change lands on both
 * screens at once.
 *
 * - UNPLUGGED: the device is not plugged in; nothing to do but plug it.
 * - PLUGGED: the device is already streaming, shown while the tutorial is skipped.
 * - TAKE: the patient is asked to pick the device up (entry point of the tutorial).
 * - TURN_ON: the patient slides the switch on; the kiosk waits for the device to appear
 *   and warns that it may not work when it is still missing after WARNING_MEDIA_DEVICE_TIMER.
 * - LIGHT: brightness wheel to the maximum, auto-advances after the step timer.
 * - PLACING: the patient places the device on the area to show, auto-advances too.
 * - FOCUS: sharpness wheel, last numbered step.
 * - TURN_OFF: the patient switches the device off, or puts it down when it has no switch.
 * - FOLLOW_INSTRUCTIONS: devices without a tutorial (stethoscopes), driven by the doctor.
 */
export const TUTORIAL_STEPS: Record<TUTO_STEP, TutorialStepDescriptor> = {
  [TUTO_STEP.UNPLUGGED]: {
    header: "",
    step: 0,
    images: (label) => [deviceImage(label)],
    text: (languages, label) =>
      `<b class="uppercase-first-letter">${deviceName(label)}</b><br /> ${languages.isNotTurnOn}`,
    description: () => "",
    error: (label) => plainText(`${deviceName(label)} ${fr.isNotTurnOn}`),
  },
  [TUTO_STEP.PLUGGED]: {
    header: "",
    step: 0,
    images: (label) => [deviceImage(label)],
    text: (languages, label) =>
      `${languages.inUsing}<br /><b>${deviceName(label)}</b>`,
    description: (label) => plainText(`${fr.inUsing} ${deviceName(label)}`),
    error: () => "",
  },
  [TUTO_STEP.TAKE]: {
    header: "Tutoriel",
    step: 1,
    images: (label) => [deviceImage(label)],
    text: (languages, label) => `${languages.take}<br /><b>${deviceName(label)}</b>`,
    description: (label) => plainText(`${fr.take} ${deviceName(label)}`),
    error: () => "",
  },
  [TUTO_STEP.TURN_ON]: {
    header: "",
    step: 0,
    images: (label) => [tutorialImage(label, "turn_on")],
    text: (languages, label) =>
      label === DEVICE_ID_OTOSCOPE
        ? languages.turnOnDevice_otoscope_html
        : languages.turnOnDevice_dermatoscope_html,
    description: (label) =>
      plainText(
        label === DEVICE_ID_OTOSCOPE
          ? fr.turnOnDevice_otoscope_html
          : fr.turnOnDevice_dermatoscope_html,
      ),
    error: (label, deviceWarning) =>
      deviceWarning ? plainText(fr.seemsDeviceNotOn(deviceName(label, "short"))) : "",
  },
  [TUTO_STEP.LIGHT]: {
    header: "1/3",
    step: 2,
    images: (label) => [tutorialImage(label, "light")],
    text: (languages) => languages.lightDevice_html,
    description: () => plainText(fr.lightDevice_html),
    error: () => "",
  },
  [TUTO_STEP.PLACING]: {
    header: "2/3",
    step: 3,
    images: (label) => [tutorialImage(label, "use")],
    text: (languages, label) =>
      languages.useVideoDevice_html(deviceName(label, "naming")),
    description: (label) =>
      plainText(fr.useVideoDevice_html(deviceName(label, "naming"))),
    error: () => "",
  },
  [TUTO_STEP.FOCUS]: {
    header: "3/3",
    step: 4,
    images: (label) => [tutorialImage(label, "focus")],
    text: (languages) => languages.focusDevice_html,
    description: () => plainText(fr.focusDevice_html),
    error: () => "",
  },
  [TUTO_STEP.TURN_OFF]: {
    header: "",
    step: 0,
    images: (label) => [
      tutorialImage(label, CANNOT_TURN_OFF.includes(label) ? "put_down" : "turn_off"),
    ],
    text: (languages, label) =>
      CANNOT_TURN_OFF.includes(label)
        ? languages.putDown(deviceName(label))
        : languages.turnOffDevice_html(deviceName(label, "short")),
    description: (label) =>
      plainText(
        CANNOT_TURN_OFF.includes(label)
          ? fr.putDown(deviceName(label))
          : fr.turnOffDevice_html(deviceName(label, "short")),
      ),
    error: () => "",
  },
  [TUTO_STEP.FOLLOW_INSTRUCTIONS]: {
    header: "",
    step: 0,
    images: (label) => [tutorialImage(label, "use")],
    text: (languages, label) =>
      languages.followingInstructions_html(deviceName(label)),
    description: (label) => plainText(fr.followingInstructions_html(deviceName(label))),
    error: () => "",
  },
}

export interface TutorialState {
  current: {
    tool: string
    tool_label: string
    description: string
    images: string[]
    step: number
    totalStep: number
    local_languages: string
    error: string
  }
}

export const tutorialState = (
  tutoStep: TUTO_STEP,
  label: string,
  currentLang: string,
  deviceWarning = false,
): TutorialState | null => {
  const descriptor = TUTORIAL_STEPS[tutoStep]
  if (!descriptor) return null

  return {
    current: {
      tool: deviceName(label, "naming"),
      tool_label: deviceName(label).trim(),
      description: descriptor.description(label),
      images: descriptor.images(label).filter(Boolean),
      step: descriptor.step,
      totalStep: TUTORIAL_TOTAL_STEP,
      local_languages: currentLang,
      error: descriptor.error(label, deviceWarning),
    },
  }
}
